/**
 * Everything under this prefix is reachable without signing in.
 *
 * One prefix rather than a list of individual public paths, so the rule is decidable from a URL
 * alone — `main.tsx` checks it before MSAL is constructed, and the same single rule is what gets
 * written into whatever sits in front of the app (nginx, an Azure access restriction). A public
 * route named ad hoc would have to be repeated in each of those places and would be forgotten in
 * one of them.
 *
 * Note this is a *routing* prefix, unrelated to Vite's `public/` directory: the contents of that
 * folder are served from the site root, not from `/public/`.
 */
export const publicPathPrefix = '/public';

/** The candidate-facing CT skills assessment. */
export const ctInterviewPath = `${publicPathPrefix}/ct-interview`;

/** The observation agreement, signed by anyone shadowing in a clinic who does not work here. */
export const observationAgreementPath = `${publicPathPrefix}/observation-agreement`;

/** Whether a pathname belongs to the public, sign-in-free part of the app. */
export function isPublicPath(pathname: string): boolean {
  return pathname === publicPathPrefix || pathname.startsWith(`${publicPathPrefix}/`);
}

/** One sign-in-free page, as the hub's CT Interview Forms (Public) section lists it for handing out. */
export interface PublicForm {
  id: string;
  path: string;
  label: string;

  /** Who fills it in and what it is for — the line somebody reads before sending the link on. */
  description: string;

  /** Where its submissions are read inside the hub. Null for a form with no dashboard. */
  dashboardPath: string | null;
}

/**
 * Every public page, in one list.
 *
 * It lives beside the paths rather than in the section that renders it, so adding a third public
 * form is one entry next to its route instead of a route here and a card to remember somewhere else.
 * Nothing on the public side imports it — `main.tsx` reaches this module before MSAL exists and uses
 * only `isPublicPath`, and this array adds no imports of its own, so it stays free of anything that
 * could fail on that path.
 */
export const publicForms: PublicForm[] = [
  {
    id: 'ct-interview',
    path: ctInterviewPath,
    label: 'CT Candidate Skills Assessment',
    description:
      'The written assessment a Clinical Team candidate sits at the front desk before an interview.',
    dashboardPath: '/ct-interview-review',
  },
  {
    id: 'observation-agreement',
    path: observationAgreementPath,
    label: 'Chiropractic Office Observation Agreement',
    description:
      'The job-shadowing agreement anyone observing in a clinic signs if they do not work here.',
    dashboardPath: '/observation-agreement-review',
  },
];
